import { useI18n } from '../i18n';
import { useAppStore } from '../state/store';
import { useHaptics } from '../hooks/useHaptics';

interface Props {
  onStart: () => void | Promise<void>;
  onStop: () => void | Promise<void>;
}

export default function StartStopButton({ onStart, onStop }: Props) {
  const { t } = useI18n();
  const audioState = useAppStore((s) => s.audioState);
  const { toggle } = useHaptics();

  const running = audioState === 'running' || audioState === 'starting';
  // Label changes between Start/Stop so screen readers always hear the
  // action — avoid aria-pressed to prevent "selected/pressed" being
  // announced on top of the already explicit label.
  const label = running ? t('button.stop.a11y') : t('button.start.a11y');

  const handleClick = async () => {
    toggle();
    if (running) await onStop();
    else await onStart();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={[
        'relative flex items-center justify-center',
        'h-[180px] w-[180px] rounded-full',
        'shadow-[0_18px_40px_-12px_rgba(0,0,0,0.35)]',
        'transition-colors duration-150',
        'active:scale-[0.97]',
        running
          ? 'bg-danger text-white focus-visible:ring-danger-dark'
          : 'bg-accent text-white focus-visible:ring-accent-dark',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
      ].join(' ')}
    >
      {running ? (
        <svg viewBox="0 0 24 24" className="h-20 w-20" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-20 w-20" aria-hidden="true">
          <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}
